import { FC, useEffect, useMemo, useState } from "react";
import {
  FormController,
  MuiGrid,
  MuiSelect,
  MuiTextField,
} from "../../../../../../../../components";
import { useSiteCore } from "../../../../../../../../hooks/useSiteCore";
import { programSiteCoreKeys } from "../../../../../../siteCore/SiteCoreKeys";

import { useFormContext } from "react-hook-form";
import { useDispatch } from "react-redux";
import { useGetHrAndBillingContactsMutation } from "../../../../../../../../api/services/client/clientApis";
import { useGetDeliveryOfficeListQuery } from "../../../../../../../../api/services/mdmOffice/mdmOfficeApis";
import { useGetUsersDetailByRoleMutation } from "../../../../../../../../api/services/user/userApi";
import {
  MAX_LENGTH_75,
  ROLE_IDS,
  SOLUTION_OFFERING_CMC_SITECORE_KEY,
  TM_INDIVIDUAL_ASSESSMENT,
  TM_LEADER_COACHING,
} from "../../../../../../../../helpers/constants";
import { UserDetails } from "../../../../../../../../helpers/types/common";
import {
  getCountryCodeWithoutCountryPrefix,
  isNotEmpty,
  mapDropdownDataWithName,
  setApiDataToDropDownOption,
} from "../../../../../../../../helpers/utils";
import { useGridContainer } from "../../../../../../../../hooks/useGridContainer";
import { useNavigationSiteCore } from "../../../../../../../../hooks/useNavigationSiteCore";
import {
  toggleLoader,
  toggleToast,
} from "../../../../../../../../redux/slices/appState/appStateSlice";
import { isDeliveryCountryJapanNetherlands } from "../../../../../../helper";
import {
  ProgramDetail,
  ProgramTabFormKey,
} from "../../../../../../modelsAndSchemas/Models";

interface ProgramDetailProps {
  pageId: string;
  programData?: ProgramDetail;
  isFieldDisabled: boolean;
  isFromTransfer?: boolean;
  isFromCoachPortal?: boolean;
}

export const ProgramOtherDetail: FC<ProgramDetailProps> = ({
  pageId,
  programData,
  isFieldDisabled,
  isFromTransfer = false,
  isFromCoachPortal = false,
}) => {
  const dispatch = useDispatch();
  const { control, getValues, setValue, watch } = useFormContext();
  const { gridSizeMedium: gridSize, gridSizeMediumClass } = useGridContainer();
  const [additionalInvoiceContact, setAdditionalInvoiceContact] = useState<
    UserDetails[]
  >([]);

  const [additionalHrContact, setAdditionalHrContact] = useState<UserDetails[]>(
    [],
  );

  const { fetchLabel, commonLabels } = useSiteCore();
  const { commonARIALabel } = useNavigationSiteCore();
  const {
    mutateAsync: getLocalClientData,
    data: localClientRelationshipOwnerData,
    isPending: isRelationshipOwnerFetching,
    isError: isLocalClientRelationshipOwnerError,
  } = useGetUsersDetailByRoleMutation();

  const {
    mutateAsync: getHrContacts,
    data: hrContacts,
    isPending: isHrContactsPending,
    isError: isHrContactsError,
  } = useGetHrAndBillingContactsMutation();

  const {
    mutateAsync: getBillingContacts,
    data: billingContacts,
    isPending: isBillingContactsPending,
    isError: isBillingContactsError,
  } = useGetHrAndBillingContactsMutation();

  const {
    mutateAsync: getManagerContacts,
    data: participantManagerContacts,
    isPending: isParticipantManagerPending,
    isError: isParticipantManagerError,
  } = useGetHrAndBillingContactsMutation();

  const {
    mutateAsync: getProjectManagerData,
    data: projectManagerList,
    isPending: isManagerPending,
    isError: isManagerError,
  } = useGetUsersDetailByRoleMutation();

  const {
    mutateAsync: getCoordinatorData,
    data: projectCoordinatorList,
    isPending: isCoordinatorPending,
    isError: isCoordinatorError,
  } = useGetUsersDetailByRoleMutation();

  const {
    mutateAsync: getDeliveryLeadEngagementManagerData,
    data: deliveryLeadEngagementManagerList,
    isPending: isDeliveryLeadEngagementManagerPending,
    isError: isDeliveryLeadEngagementManagerError,
  } = useGetUsersDetailByRoleMutation();

  const {
    data: deliveryCountryList,
    refetchWithParams: refetchDeliveryCountryList,
  } = useGetDeliveryOfficeListQuery();

  useEffect(() => {
    if (isNotEmpty(programData))
      refetchDeliveryCountryList(
        getCountryCodeWithoutCountryPrefix(
          programData?.deliveryCountry,
        ).toUpperCase(),
      );
  }, [programData]);

  useEffect(() => {
    const { deliveryOffice } = getValues();
    if (isNotEmpty(deliveryCountryList) && isNotEmpty(deliveryOffice)) {
      const exists = deliveryCountryList.some(
        (office) => String(office.id) === String(deliveryOffice),
      );

      if (!exists) {
        setValue(ProgramTabFormKey.deliveryOffice, "");
      }
    }
  }, [watch(ProgramTabFormKey.deliveryOffice), deliveryCountryList]);

  useEffect(() => {
    updateAdditionalContactForInvoice();
  }, [billingContacts]);

  useEffect(() => {
    updateAdditionalHrContact();
  }, [hrContacts]);

  const showBusinessApprovalField = useMemo(() => {
    return isDeliveryCountryJapanNetherlands(programData?.deliveryCountry);
  }, [programData?.deliveryCountry]);

  useEffect(() => {
    dispatch(
      toggleLoader({
        otherDetails: {
          isVisible:
            isRelationshipOwnerFetching ||
            isHrContactsPending ||
            isBillingContactsPending ||
            isManagerPending ||
            isCoordinatorPending ||
            isParticipantManagerPending ||
            isDeliveryLeadEngagementManagerPending,
          message: commonLabels.loadingPleaseWait,
        },
      }),
    );
    return () => {
      dispatch(toggleLoader({ otherDetails: { isVisible: false } }));
    };
  }, [
    isRelationshipOwnerFetching,
    isHrContactsPending,
    isBillingContactsPending,
    isManagerPending,
    isCoordinatorPending,
    isParticipantManagerPending,
    isDeliveryLeadEngagementManagerPending,
  ]);

  useEffect(() => {
    if (
      isLocalClientRelationshipOwnerError ||
      isHrContactsError ||
      isBillingContactsError ||
      isCoordinatorError ||
      isManagerError ||
      isParticipantManagerError ||
      isDeliveryLeadEngagementManagerError
    ) {
      dispatch(
        toggleToast({
          open: true,
          message: commonLabels.someThingWentWrongPleaseTryAgainLater,
          type: "error",
        }),
      );
      dispatch(toggleLoader({ isVisible: false }));
    }
  }, [
    isLocalClientRelationshipOwnerError,
    isHrContactsError,
    isBillingContactsError,
    isCoordinatorError,
    isManagerError,
    isParticipantManagerError,
    isDeliveryLeadEngagementManagerError,
  ]);

  useEffect(() => {
    if (isNotEmpty(programData)) {
      const clientId = programData?.clientId ?? "";
      getHrContacts({
        clientId,
        contactType: "hr",
      });
      getBillingContacts({
        clientId,
        contactType: "billing",
      });
      getManagerContacts({
        clientId,
        contactType: "manager",
      });
      getLocalClientData({ roleIds: [ROLE_IDS.sales] });
      getCoordinatorData({ roleIds: [ROLE_IDS.projectCoOrdinator] });
      getProjectManagerData({ roleIds: [ROLE_IDS.projectManager] });
      if (isTMAssessmentProgram || isTMLeaderCoachingProgram)
        getDeliveryLeadEngagementManagerData({
          roleIds: [ROLE_IDS.leadConsultant],
        });
    }
  }, [programData?.clientId]);

  const getLabel = (key: string) => {
    return fetchLabel(programSiteCoreKeys.officeDetailsKey, key);
  };

  const isCMCProgram = useMemo(() => {
    return (
      programData?.solutionOfferingSiteCoreKey ===
      SOLUTION_OFFERING_CMC_SITECORE_KEY
    );
  }, [programData?.solutionOfferingSiteCoreKey]);

  const isTMAssessmentProgram = useMemo(() => {
    return programData?.tmProgramType === TM_INDIVIDUAL_ASSESSMENT;
  }, [programData?.tmProgramType]);

  const isTMLeaderCoachingProgram = useMemo(() => {
    return programData?.tmProgramType === TM_LEADER_COACHING;
  }, [programData?.tmProgramType]);

  const getFilteredContacts = (
    contacts: UserDetails[] | undefined,
    selectedContactId: string,
  ) => {
    return contacts
      ? contacts.filter((contact) => contact.id !== selectedContactId)
      : [];
  };

  const updateDependentContact = (
    dependentFieldName: string,
    selectedContactId: string,
  ) => {
    const selectedDependentContactId = getValues(dependentFieldName);
    if (selectedContactId === selectedDependentContactId) {
      setValue(dependentFieldName, "");
    }
  };

  const getFilteredAdditionalContactForInvoice = () => {
    const selectedBillingContact = getValues(ProgramTabFormKey.billingContact);
    setAdditionalInvoiceContact(
      getFilteredContacts(billingContacts, selectedBillingContact),
    );
  };

  const updateAdditionalContactForInvoice = () => {
    const selectedBillingContact = getValues(ProgramTabFormKey.billingContact);
    updateDependentContact(
      ProgramTabFormKey.additionalInvoiceContactId,
      selectedBillingContact,
    );
    getFilteredAdditionalContactForInvoice();
  };

  const getFilteredAdditionalHrContact = () => {
    const selectedHrContact = getValues(ProgramTabFormKey.hrContact);
    setAdditionalHrContact(getFilteredContacts(hrContacts, selectedHrContact));
  };

  const updateAdditionalHrContact = () => {
    const selectedHrContact = getValues(ProgramTabFormKey.hrContact);
    updateDependentContact(
      ProgramTabFormKey.additionalHRContactId,
      selectedHrContact,
    );
    getFilteredAdditionalHrContact();
  };

  const isFieldDisabledOrFromCoachPortal = isFieldDisabled || isFromCoachPortal;

  const renderCommonTMFields = () => (
    <>
      <MuiGrid size={gridSize} className={gridSizeMediumClass}>
        <FormController
          control={control}
          name={ProgramTabFormKey.deliveryLeadEngagementManagerId}
        >
          <MuiSelect
            label={getLabel(programSiteCoreKeys.deliveryLead)}
            options={mapDropdownDataWithName(deliveryLeadEngagementManagerList)}
            visibleDefaultSelectOption
            id={`${pageId}-${ProgramTabFormKey.deliveryLeadEngagementManagerId}`}
            placeholder={commonLabels.selectPlaceHolder}
            searchPlaceholder={commonLabels.search}
            requiredARIALabel={commonARIALabel.required}
            disabled={isFieldDisabledOrFromCoachPortal}
          />
        </FormController>
      </MuiGrid>

      <MuiGrid size={gridSize} className={gridSizeMediumClass}>
        <FormController
          control={control}
          name={ProgramTabFormKey.additionalInvoiceContactId}
        >
          <MuiSelect
            label={getLabel(programSiteCoreKeys.additionalInvoiceContact)}
            options={mapDropdownDataWithName(additionalInvoiceContact)}
            visibleDefaultSelectOption
            id={`${pageId}-${ProgramTabFormKey.additionalInvoiceContactId}`}
            placeholder={commonLabels.selectPlaceHolder}
            searchPlaceholder={commonLabels.search}
            requiredARIALabel={commonARIALabel.required}
            disabled={isFieldDisabledOrFromCoachPortal}
          />
        </FormController>
      </MuiGrid>

      <MuiGrid size={gridSize} className={gridSizeMediumClass}>
        <FormController
          control={control}
          name={ProgramTabFormKey.participantManagerId}
        >
          <MuiSelect
            label={getLabel(programSiteCoreKeys.participantsManager)}
            options={mapDropdownDataWithName(participantManagerContacts)}
            visibleDefaultSelectOption
            id={`${pageId}-${ProgramTabFormKey.participantManagerId}`}
            placeholder={commonLabels.selectPlaceHolder}
            searchPlaceholder={commonLabels.search}
            requiredARIALabel={commonARIALabel.required}
            disabled={isFieldDisabledOrFromCoachPortal}
          />
        </FormController>
      </MuiGrid>
    </>
  );

  return (
    <MuiGrid
      container
      className="form-container program-detail"
      data-testid="program-other-detail"
    >
      <MuiGrid size={gridSize} className={gridSizeMediumClass}>
        <FormController
          control={control}
          name={ProgramTabFormKey.billingOffice}
        >
          <MuiTextField
            label={getLabel(programSiteCoreKeys.billingOffice)}
            value=""
            id={`${pageId}-${ProgramTabFormKey.billingOffice}`}
            placeholder={commonLabels.inputPlaceHolder}
            disabled
            requiredARIALabel={commonARIALabel.required}
          />
        </FormController>
      </MuiGrid>

      <MuiGrid size={gridSize} className={gridSizeMediumClass}>
        <FormController control={control} name={ProgramTabFormKey.salesOffice}>
          <MuiTextField
            label={getLabel(programSiteCoreKeys.salesOfficeDetail)}
            value=""
            id={`${pageId}-${ProgramTabFormKey.salesOffice}`}
            placeholder={commonLabels.inputPlaceHolder}
            disabled
            requiredARIALabel={commonARIALabel.required}
          />
        </FormController>
      </MuiGrid>

      <MuiGrid size={gridSize} className={gridSizeMediumClass}>
        <FormController
          control={control}
          name={ProgramTabFormKey.clientRelationShipOwner}
        >
          <MuiSelect
            label={getLabel(programSiteCoreKeys.relationshipOwner)}
            options={mapDropdownDataWithName(localClientRelationshipOwnerData)}
            visibleDefaultSelectOption
            id={`${pageId}-${ProgramTabFormKey.clientRelationShipOwner}`}
            placeholder={commonLabels.selectPlaceHolder}
            searchPlaceholder={commonLabels.search}
            isMandatory={!isFieldDisabled}
            disabled={isFieldDisabledOrFromCoachPortal}
            requiredARIALabel={commonARIALabel.required}
          />
        </FormController>
      </MuiGrid>

      <MuiGrid size={gridSize} className={gridSizeMediumClass}>
        <FormController
          control={control}
          name={ProgramTabFormKey.deliveryOffice}
        >
          <MuiSelect
            label={getLabel(programSiteCoreKeys.deliveryOffice)}
            options={setApiDataToDropDownOption(deliveryCountryList)}
            visibleDefaultSelectOption
            id={`${pageId}-${ProgramTabFormKey.deliveryOffice}`}
            placeholder={commonLabels.selectPlaceHolder}
            searchPlaceholder={commonLabels.search}
            isMandatory={!isFieldDisabled}
            disabled={
              isFromCoachPortal || (isFromTransfer ? false : isFieldDisabled)
            }
            requiredARIALabel={commonARIALabel.required}
          />
        </FormController>
      </MuiGrid>

      <MuiGrid size={gridSize} className={gridSizeMediumClass}>
        <FormController
          control={control}
          name={ProgramTabFormKey.billingContact}
        >
          <MuiSelect
            label={getLabel(programSiteCoreKeys.billingContact)}
            options={mapDropdownDataWithName(billingContacts)}
            visibleDefaultSelectOption
            id={`${pageId}-${ProgramTabFormKey.billingContact}`}
            placeholder={commonLabels.selectPlaceHolder}
            searchPlaceholder={commonLabels.search}
            isMandatory={!isFieldDisabled}
            disabled={isFieldDisabledOrFromCoachPortal}
            requiredARIALabel={commonARIALabel.required}
            onChange={updateAdditionalContactForInvoice}
          />
        </FormController>
      </MuiGrid>

      <MuiGrid size={gridSize} className={gridSizeMediumClass}>
        <FormController control={control} name={ProgramTabFormKey.hrContact}>
          <MuiSelect
            label={getLabel(programSiteCoreKeys.hrContact)}
            options={mapDropdownDataWithName(hrContacts)}
            visibleDefaultSelectOption
            id={`${pageId}-${ProgramTabFormKey.hrContact}`}
            placeholder={commonLabels.selectPlaceHolder}
            searchPlaceholder={commonLabels.search}
            isMandatory={!isFieldDisabled}
            disabled={isFieldDisabledOrFromCoachPortal}
            requiredARIALabel={commonARIALabel.required}
            onChange={updateAdditionalHrContact}
          />
        </FormController>
      </MuiGrid>

      {isTMAssessmentProgram && (
        <MuiGrid size={gridSize} className={gridSizeMediumClass}>
          <FormController
            control={control}
            name={ProgramTabFormKey.additionalHRContactId}
          >
            <MuiSelect
              label={getLabel(programSiteCoreKeys.additionalHrContact)}
              options={mapDropdownDataWithName(additionalHrContact)}
              visibleDefaultSelectOption
              id={`${pageId}-${ProgramTabFormKey.additionalHRContactId}`}
              placeholder={commonLabels.selectPlaceHolder}
              searchPlaceholder={commonLabels.search}
              requiredARIALabel={commonARIALabel.required}
              disabled={isFieldDisabledOrFromCoachPortal}
            />
          </FormController>
        </MuiGrid>
      )}

      <MuiGrid size={gridSize} className={gridSizeMediumClass}>
        <FormController
          control={control}
          name={ProgramTabFormKey.projectManager}
        >
          <MuiSelect
            label={getLabel(programSiteCoreKeys.projectManager)}
            options={mapDropdownDataWithName(projectManagerList)}
            visibleDefaultSelectOption
            id={`${pageId}-${ProgramTabFormKey.projectManager}`}
            placeholder={commonLabels.selectPlaceHolder}
            searchPlaceholder={commonLabels.search}
            disabled={isFieldDisabledOrFromCoachPortal}
            requiredARIALabel={commonARIALabel.required}
          />
        </FormController>
      </MuiGrid>

      <MuiGrid size={gridSize} className={gridSizeMediumClass}>
        <FormController
          control={control}
          name={ProgramTabFormKey.projectCoordinator}
        >
          <MuiSelect
            label={getLabel(programSiteCoreKeys.projectCoordinator)}
            options={mapDropdownDataWithName(projectCoordinatorList)}
            visibleDefaultSelectOption
            id={`${pageId}-${ProgramTabFormKey.projectCoordinator}`}
            placeholder={commonLabels.selectPlaceHolder}
            searchPlaceholder={commonLabels.search}
            disabled={isFieldDisabledOrFromCoachPortal}
            requiredARIALabel={commonARIALabel.required}
          />
        </FormController>
      </MuiGrid>

      {showBusinessApprovalField && (
        <MuiGrid size={gridSize} className={gridSizeMediumClass}>
          <FormController
            control={control}
            name={ProgramTabFormKey.businessApprovalNumber}
          >
            <MuiTextField
              label={getLabel(programSiteCoreKeys.businessApprovalNumber)}
              id={`${pageId}-${ProgramTabFormKey.businessApprovalNumber}`}
              placeholder={commonLabels.inputPlaceHolder}
              requiredARIALabel={commonARIALabel.required}
              maxLength={MAX_LENGTH_75}
              disabled={isFromCoachPortal}
            />
          </FormController>
        </MuiGrid>
      )}

      {isCMCProgram && (
        <>
          <MuiGrid size={gridSize} className={gridSizeMediumClass}>
            <FormController
              control={control}
              name={ProgramTabFormKey.additionalInvoiceContactId}
            >
              <MuiSelect
                label={getLabel(programSiteCoreKeys.additionalInvoiceContact)}
                options={mapDropdownDataWithName(billingContacts)}
                visibleDefaultSelectOption
                id={`${pageId}-${ProgramTabFormKey.additionalInvoiceContactId}`}
                placeholder={commonLabels.selectPlaceHolder}
                searchPlaceholder={commonLabels.search}
                requiredARIALabel={commonARIALabel.required}
                disabled={isFieldDisabledOrFromCoachPortal}
              />
            </FormController>
          </MuiGrid>

          <MuiGrid size={gridSize} className={gridSizeMediumClass}>
            <FormController
              control={control}
              name={ProgramTabFormKey.participantManagerId}
            >
              <MuiSelect
                label={getLabel(programSiteCoreKeys.participantsManager)}
                options={mapDropdownDataWithName(participantManagerContacts)}
                visibleDefaultSelectOption
                id={`${pageId}-${ProgramTabFormKey.participantManagerId}`}
                placeholder={commonLabels.selectPlaceHolder}
                searchPlaceholder={commonLabels.search}
                requiredARIALabel={commonARIALabel.required}
                disabled={isFieldDisabledOrFromCoachPortal}
              />
            </FormController>
          </MuiGrid>
        </>
      )}

      {isTMAssessmentProgram && (
        <>
          {renderCommonTMFields()}

          <MuiGrid size={gridSize} className={gridSizeMediumClass}>
            <FormController
              control={control}
              name={ProgramTabFormKey.hiringManager}
            >
              <MuiTextField
                label={getLabel(programSiteCoreKeys.hiringManager)}
                id={`${pageId}-${ProgramTabFormKey.hiringManager}`}
                placeholder={commonLabels.inputPlaceHolder}
                requiredARIALabel={commonARIALabel.required}
                maxLength={MAX_LENGTH_75}
                disabled={isFromCoachPortal}
              />
            </FormController>
          </MuiGrid>
        </>
      )}

      {isTMLeaderCoachingProgram && renderCommonTMFields()}
    </MuiGrid>
  );
};
